import { ApiError, api } from "./api";
import type { StartedSession } from "../../../../../packages/admin-api-typescript-types/src/types.gen";

/** What the page says when the sign-in service answers without a body of its
 *  own and not with a 401: the edge's bodiless 502 while auth-service is down. */
export const SIGN_IN_UNAVAILABLE = "Sign-in is temporarily unavailable. Try again in a minute.";

/** What the console says about a sign-out that did not happen. */
export const SIGN_OUT_FAILED =
  "You are still signed in: the sign-out did not go through. A service may be restarting, so try again in a moment.";

async function authRequest(path: string, json?: unknown): Promise<unknown> {
  const response = await fetch(`/auth/${path}?appId=app1`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(json === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(json === undefined ? {} : { method: "POST", body: JSON.stringify(json) }),
  });
  const body: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const error = typeof body === "object" && body !== null
      ? body as Record<string, unknown>
      : {};
    // Only a 401 refuses the person; any other bodiless answer is an outage.
    const refused = response.status === 401;
    throw new ApiError(
      response.status,
      typeof error.code === "string" ? error.code : refused ? "unauthorized" : "service_unavailable",
      typeof error.message === "string" ? error.message : refused ? undefined : SIGN_IN_UNAVAILABLE,
    );
  }
  return body;
}

let renewal: Promise<StartedSession> | undefined;
let signedOut = false;

/** Concurrent requests share the same login token and console session. */
export function renew(): Promise<StartedSession> {
  if (signedOut) return Promise.reject(new ApiError(401, "unauthorized"));
  if (!renewal) {
    renewal = (async () => {
      const body = await authRequest("token");
      if (
        typeof body !== "object" || body === null ||
        !("token" in body) || typeof body.token !== "string" || body.token === ""
      ) {
        throw new ApiError(401, "unauthorized");
      }
      return api.signIn(body.token);
    })().finally(() => { renewal = undefined; });
  }
  return renewal;
}

export async function signIn(email: string, password: string): Promise<StartedSession> {
  await authRequest("sign-in/email", { email, password });
  signedOut = false;
  return renew();
}

/**
 * Asks the sign-in service to mail the signed-in person a new link to their own
 * address: the body names none, this origin's login session says whose.
 * `confirmed` when the address already is, `signed-out` with no live session,
 * `unavailable` for a failed send, any other answer, or none.
 */
export async function requestOwnProof(): Promise<"sent" | "confirmed" | "signed-out" | "unavailable"> {
  try {
    await authRequest("proof/request", {});
    return "sent";
  } catch (failure) {
    if (!(failure instanceof ApiError)) return "unavailable";
    return failure.status === 409 ? "confirmed" : failure.status === 401 ? "signed-out" : "unavailable";
  }
}

/**
 * Ends the Noctant login, then the console session. Rejects when either still
 * stands, and the page says so: only a 2xx from the sign-in service proves the
 * login ended, and a console session revoked over a login that lives is
 * renewed from its cookie at the next load, with no password.
 */
export async function signOut(): Promise<void> {
  signedOut = true;
  // An exchange already in flight can set a cookie; revoke only after it settles.
  await renewal?.catch(() => undefined);
  try {
    await authRequest("sign-out", {});
  } catch (failure) {
    signedOut = false;
    throw failure;
  }
  await api.logout();
}
